import { createPlugin } from '@customerio/dryad';
import { ConditionType } from './condition-types';
import {
  isTwoValueFilterOperator,
  isUnaryFilterOperator,
  parseAttributeEnvelope,
  unwrapSupportableAttributeEnvelope,
  validateAttributeFilter,
  type AttributeFilterState,
} from './attribute-filter';
import { isOperatorInFamilies } from './operators';
import { PEOPLE_SEARCH_OPERATOR_FAMILIES } from './attribute-plugin';
import type { DeviceAttributeFilter } from './wire-types';

// The proto `{ device_attribute: {...} }` leaf, negation as a `{ not: ... }`
// wrapper. Same field/operator/value state as the attribute rows, so the
// shared attribute-filter state type applies, but its own wire envelope and
// its own serializer: this leaf is emitted with nullish keys dropped and no
// value trimming, unlike the `{ attribute }` leaf's serializer, which trims —
// the Ember app writes the same bytes for the same edit, and both apps serve
// the page while `profiles-migration` rolls out.
//
// Gated on the same equality + existence families as the people attribute
// plugin: every special device attribute the editor offers here (platform,
// id, app_identifier, _last_status, device_model) uses eq/!eq or
// exists/!exists. The one exception is `_last_used`, which this surface's
// picker filters out: a saved `_last_used` filter using a timestamp operator
// stays on the error-node passthrough (dryad's fallback for unclaimed nodes:
// re-serialized verbatim, rendered as a remove-only row), while its `!exists`
// variant ("has not been used") is in the existence family and is claimed.

// True when the node is a device_attribute leaf whose operator this surface's
// row can render and whose values are strings. A numeric value would be
// stringified in parse and break byte parity — a claimed wire must
// re-serialize byte-identically — so it stays on the error-node passthrough.
// fallow-ignore-next-line complexity -- a flat sequence of shape guards; splitting hurts readability
function matchesDeviceAttributeFilter(json: unknown): boolean {
  const unwrapped = unwrapSupportableAttributeEnvelope(json, 'device_attribute');
  if (unwrapped == null) {
    return false;
  }
  const { attribute, operator } = unwrapped;
  if (!isOperatorInFamilies(operator, new Set(PEOPLE_SEARCH_OPERATOR_FAMILIES))) {
    return false;
  }
  if (!isUnaryFilterOperator(operator) && attribute.value != null && typeof attribute.value !== 'string') {
    return false;
  }
  if (attribute.value2 != null && (!isTwoValueFilterOperator(operator) || typeof attribute.value2 !== 'string')) {
    return false;
  }
  return true;
}

// Nullish and empty-string keys are pruned and values stay untrimmed — the
// exact bytes the backend's decoder and the Ember app expect for this leaf.
// Key insertion order (field, value, value2, operator) is part of the byte
// contract.
function prunedDeviceAttribute(state: AttributeFilterState, wireOperator: string) {
  const attribute: DeviceAttributeFilter['device_attribute'] = {};
  if (state.field !== '') {
    attribute.field = state.field;
  }
  const value = isUnaryFilterOperator(state.operator) ? null : state.value;
  if (value != null && value !== '') {
    attribute.value = value;
  }
  const value2 = isTwoValueFilterOperator(state.operator) ? state.value2 : null;
  if (value2 != null && value2 !== '') {
    attribute.value2 = value2;
  }
  if (wireOperator !== '') {
    attribute.operator = wireOperator;
  }
  return attribute;
}

// '!' operators become a `{ not }` wrapper around the leaf.
function serializeDeviceAttributeFilter(
  state: AttributeFilterState,
): DeviceAttributeFilter | { not: DeviceAttributeFilter } {
  const negated = state.operator.startsWith('!');
  const wireOperator = negated ? state.operator.slice(1) : state.operator;

  const filter: DeviceAttributeFilter = { device_attribute: prunedDeviceAttribute(state, wireOperator) };
  if (negated) {
    return { not: filter };
  }
  return filter;
}

export const peopleFilterDeviceAttributePlugin = createPlugin({
  type: ConditionType.DeviceAttribute,
  label: 'Device',
  parse: (json: unknown) =>
    matchesDeviceAttributeFilter(json) ? parseAttributeEnvelope(json, 'device_attribute') : null,
  serialize: serializeDeviceAttributeFilter,
  validate: validateAttributeFilter,
  createDefault: (): AttributeFilterState => ({ field: '', operator: 'eq', value: null, value2: null }),
});
